import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ExecClient } from "lib/shell/ExecClient";
import { AnsiText } from "./AnsiText";
import { useCommandHistory } from "./useCommandHistory";

/** A single entry in the terminal scrollback. */
type ShellLine =
  | { kind: "command"; text: string }
  | { kind: "output"; text: string }
  | { kind: "error"; text: string }
  | { kind: "exit"; code: number };

type DeviceShellProps = {
  /** Transport used to run each command. */
  execClient: ExecClient;
  /** Scopes the persisted command history; usually the storage ID. */
  historyScope: string;
  /** Shown in the prompt, e.g. `bench-01 $`. */
  promptLabel?: string;
  /** Rendered above the scrollback as the window title bar. */
  header?: ReactNode;
};

let nextLineID = 0;

const ShellLineView = ({ line, promptLabel }: { line: ShellLine; promptLabel: string }) => {
  if (line.kind === "command") {
    return (
      <div className="whitespace-pre-wrap break-words">
        <span className="text-primary-200">{promptLabel} $</span> <span className="text-neutral-100">{line.text}</span>
      </div>
    );
  }
  if (line.kind === "output") {
    return (
      <div className="whitespace-pre-wrap break-words">
        <AnsiText>{line.text}</AnsiText>
      </div>
    );
  }
  if (line.kind === "error") {
    return <div className="whitespace-pre-wrap break-words text-red-400">{line.text}</div>;
  }
  return <div className="text-neutral-500">exit {line.code}</div>;
};

/**
 * DeviceShell is a one-shot command console. Every submitted line runs as `/bin/sh -c "<line>"`
 * on the device, so single-line shell syntax works but no state carries between commands.
 */
export const DeviceShell = ({ execClient, historyScope, promptLabel = "device", header }: DeviceShellProps) => {
  const [lines, setLines] = useState<Array<ShellLine & { id: number }>>([]);
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useCommandHistory(historyScope);

  const append = useCallback((...entries: ShellLine[]) => {
    setLines((current) => [...current, ...entries.map((entry) => ({ ...entry, id: nextLineID++ }))]);
  }, []);

  // Keep the newest output in view as the scrollback grows.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [lines, running]);

  // Return focus to the prompt once a command finishes.
  useEffect(() => {
    if (!running) inputRef.current?.focus();
  }, [running]);

  const run = useCallback(async () => {
    const command = input.trim();
    if (!command || running) return;

    history.push(command);
    history.reset();
    append({ kind: "command", text: command });
    setInput("");
    setRunning(true);

    const result = await execClient.exec("/bin/sh", ["-c", command]);

    const entries: ShellLine[] = [];
    if (result.output) entries.push({ kind: "output", text: result.output.replace(/\n$/, "") });
    if (result.error) entries.push({ kind: "error", text: result.error });
    if (!result.error && result.exitCode !== 0) entries.push({ kind: "exit", code: result.exitCode });
    append(...entries);
    setRunning(false);
  }, [append, execClient, history, input, running]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void run();
      return;
    }
    if (event.key === "l" && event.ctrlKey) {
      event.preventDefault();
      setLines([]);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      const previous = history.previous();
      if (previous !== undefined) setInput(previous);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = history.next();
      if (next !== undefined) setInput(next);
    }
  };

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl bg-[#0b0e14] ring-1 ring-white/10 shadow-2xl"
      onClick={() => inputRef.current?.focus()}
    >
      {header}
      <div
        ref={scrollRef}
        role="log"
        aria-live="polite"
        aria-label="Command output"
        className="flex-1 min-h-0 overflow-y-auto px-5 py-4 font-mono text-[13px] leading-relaxed text-neutral-300 selection:bg-primary-500/40"
      >
        {lines.map((line) => (
          <ShellLineView key={line.id} line={line} promptLabel={promptLabel} />
        ))}

        <div className="flex items-baseline gap-2 whitespace-pre-wrap break-words">
          <span className="shrink-0 text-primary-200">{promptLabel} $</span>
          {running ? (
            <span className="text-neutral-500">
              running<span className="motion-safe:animate-pulse">…</span>
            </span>
          ) : (
            <input
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Command"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-[13px] text-neutral-100 caret-primary-200 focus:outline-none focus:ring-0"
            />
          )}
        </div>
      </div>
    </div>
  );
};
